import { Center, Fieldset, Separator, VStack } from '@chakra-ui/react';
import { PlatformForm } from '@components/cockpit/panels/platformManagement/PlatformFormContainer';
import { ZoneForm, ZoneFormGeometryTypeEnum } from '@components/cockpit/panels/zoneManagement/zoneManagement.types';
import ErrorsContainer from '@components/common/form/ErrorsContainer';
import { ImportFileButton } from '@components/common/inputs/buttons/ImportFileButton';
import { GPSCoordinatesListField } from '@components/ui/coordinatesInput/list/GPSCoordinatesListField';
import { usePolygonFormMapContext } from '@components/ui/map/formMap/FormMapContext';
import { ScrollArea } from '@components/ui/ScrollArea';
import { createErrorToastFromTradId } from '@components/ui/toaster';
import { booleanValid, getGeom, getType } from '@turf/turf';
import { getFirstPolygon } from '@utils/map/polygon.utils';
import { Field, FieldProps, getIn, useFormikContext } from 'formik';
import { Feature, Polygon } from 'geojson';
import { isEqual } from 'lodash';
import { ChangeEvent, useEffect, useRef } from 'react';

type PolygonFormType = PlatformForm | ZoneForm;

export function PolygonCoordinatesInput() {
  const {
    mapRef,
    unit,
    setUnit,
    geometry,
    updateGeometry,
    isPolygonal,
    zoomOnPolygon,
    addPolygonCoordinate,
    updatePolygonCoordinate,
    deletePolygonCoordinate,
  } = usePolygonFormMapContext();

  const { values, touched, errors, setFieldValue } = useFormikContext<PolygonFormType>();

  const lastPolyRef = useRef<Polygon | undefined>(undefined);

  useEffect(() => {
    if (!isPolygonal(geometry)) {
      return;
    }

    const newPoly = getFirstPolygon(geometry);
    if (!newPoly || isEqual(newPoly, lastPolyRef.current)) {
      return;
    }

    lastPolyRef.current = newPoly;
    void setFieldValue('polygon', newPoly);
  }, [geometry, isPolygonal, setFieldValue]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !values.polygon?.coordinates[0]?.length) {
      return;
    }

    zoomOnPolygon(values.polygon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGeoJsonImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const data = JSON.parse(await file.text());
      const feature =
        data.type === 'FeatureCollection' ? data.features.find((f: Feature) => getType(f) === 'Polygon') : data;
      if (feature && getType(feature) === 'Polygon' && booleanValid(getGeom(feature))) {
        const polygon = getGeom(feature) as Polygon;
        updateGeometry(polygon);
        zoomOnPolygon(polygon);
      } else {
        createErrorToastFromTradId('components.polygonCoordinatesInput.importError');
      }
    } catch (error) {
      createErrorToastFromTradId('components.polygonCoordinatesInput.importError');
      console.error(error);
    }
  };

  const isInvalid = (key: keyof PolygonFormType) => {
    const isTouched = getIn(touched, key);
    const error = getIn(errors, key);
    return Boolean(isTouched && error);
  };

  return (
    <VStack gap={0} height="100%" width="fit-content" separator={<Separator borderColor="neutral.800" />}>
      <Center padding={2} width="100%">
        <ImportFileButton
          isIconButton={false}
          label="components.polygonCoordinatesInput.import"
          accept=".geojson,.json,application/geo+json,application/json"
          onFileChange={handleGeoJsonImport}
        />
      </Center>
      <ScrollArea>
        <Fieldset.Root invalid={isInvalid('polygon')}>
          <Fieldset.Content>
            <Field name="polygons" height="100%">
              {({ field }: FieldProps<PolygonFormType['polygon']>) => (
                <GPSCoordinatesListField
                  {...field}
                  name="polygon.coordinates[0]" // outer ring
                  unit={unit}
                  pointHasTwoLevels={'geometry' in values && values.geometry === ZoneFormGeometryTypeEnum.FINITE_HEIGHT}
                  setUnit={setUnit}
                  setPolygon={updatePolygonCoordinate}
                  deleteCoordinate={deletePolygonCoordinate}
                  addCoordinate={addPolygonCoordinate}
                />
              )}
            </Field>
          </Fieldset.Content>

          {Object.keys(errors).some((key) => {
            const error = getIn(errors, key);
            const isTouched = getIn(touched, key);
            if (typeof error === 'object' && error !== null) {
              return Object.keys(error).some((subKey) => {
                return isTouched?.[subKey] && !!error?.[subKey];
              });
            }
            return isTouched && !!error;
          }) && (
            <Center padding={2}>
              <Fieldset.ErrorText marginTop={0} minHeight="auto" padding={2} width="520px">
                <ErrorsContainer<PolygonFormType> errors={errors} touched={touched} />
              </Fieldset.ErrorText>
            </Center>
          )}
        </Fieldset.Root>
      </ScrollArea>
    </VStack>
  );
}
